import type { WantedEntry } from "@vTypes";

export type WantedCalendarEntry = {
  readonly type: string;
  readonly comment: string | null;
};

export type WantedEntriesByDate = Record<string, WantedCalendarEntry>;

export type AvoidCalendarEntry = {
  readonly shiftId: string;
  readonly comment: string | null;
};

export type AvoidEntriesByDate = Record<string, AvoidCalendarEntry>;

export function buildWantedEntries(
  wantedByDate: WantedEntriesByDate,
  avoidByDate: AvoidEntriesByDate = {}
): WantedEntry[] {
  const wantedEntries = Object.entries(wantedByDate)
    .filter(([, entry]) => entry.type && entry.comment !== "주휴")
    .map(([date, entry]) => ({
      date,
      shift_id: entry.type,
      intent: "wanted" as const,
      comment: entry.comment?.trim() || null,
    }));
  const avoidEntries = Object.entries(avoidByDate)
    .filter(([, entry]) => entry.shiftId)
    .map(([date, entry]) => ({
      date,
      shift_id: entry.shiftId,
      intent: "avoid" as const,
      comment: entry.comment?.trim() || null,
    }));

  return [...wantedEntries, ...avoidEntries].sort((left, right) =>
    left.date.localeCompare(right.date)
  );
}

export function buildSupportedWantedEntries(
  wantedByDate: WantedEntriesByDate,
  avoidByDate: AvoidEntriesByDate = {}
): WantedEntry[] {
  // 기피(avoid)도 그대로 보낸다. 백엔드가 banned_wanted_entries(source='nurse')로
  // 저장한다. 예전에는 서버가 422 로 거절해 프론트에서 걸러냈지만 지원이 붙었다.
  return buildWantedEntries(wantedByDate, avoidByDate);
}

export function splitWantedEntries(entries: readonly WantedEntry[]): {
  wantedByDate: WantedEntriesByDate;
  avoidByDate: AvoidEntriesByDate;
} {
  return entries.reduce<{
    wantedByDate: WantedEntriesByDate;
    avoidByDate: AvoidEntriesByDate;
  }>(
    (result, entry) => {
      if (!entry.date || !entry.shift_id) return result;

      if (entry.intent === "avoid") {
        result.avoidByDate[entry.date] = {
          shiftId: entry.shift_id,
          comment: entry.comment?.trim() || null,
        };
        return result;
      }

      result.wantedByDate[entry.date] = {
        type: entry.shift_id,
        comment: entry.comment?.trim() || null,
      };
      return result;
    },
    { wantedByDate: {}, avoidByDate: {} }
  );
}
